import React, { useCallback, useMemo, useState } from 'react';
import { useUniqueWcagValues } from 'common-react';
import { RetirementCalculatorBasicDataStep } from './components/RetirementCalculatorBasicDataStep/RetirementCalculatorBasicDataStep';
import { RetirementCalculatorSavingsStep } from './components/RetirementCalculatorSavingsStep/RetirementCalculatorSavingsStep';
import { RetirementCalculatorOutcomeStep } from './components/RetirementCalculatorOutcomeStep/RetirementCalculatorOutcomeStep';
import { RetirementCalculatorLayoutContext } from './components/RetirementCalculatorLayout/RetirementCalculatorLayout.context';
import {
  CALC_TEST_ID,
  DEFAULT_MONTHLY_INVESTMENT,
  MIN_MONTHLY_INVESTMENT,
  RetirementCalculatorField,
  RetirementCalculatorStep,
} from './tools/RetirementCalculator.const';
import {
  RetirementCalculatorPropsModel,
  RetirementCalculatorSettingsOverridesModel,
  RetirementCalculatorSubmitValuesModel,
  SavingsValuesModel,
} from './tools/RetirementCalculator.model';
import {
  applySettingsOverrides,
  calculateProjection,
  getMaxMonthlyInvestment,
  getPromotedProduct,
  resolveSettings,
} from './tools/RetirementCalculator.calculations';
import { useContributionLimits } from './tools/useContributionLimits';

export const RetirementCalculator: React.FC<RetirementCalculatorPropsModel> = ({
  texts,
  initialValues,
  settings,
  limitsSource,
  learnMoreUrl,
  onDownloadSummary,
  onBasicDataSubmit,
  onSavingsSubmit,
  onStepChange,
  disabled,
  isLoading,
  component = 'main',
  className,
  testId = CALC_TEST_ID,
}) => {
  const {
    title,
    submitLabel,
    savingsSubmitLabel,
    salaryTooltip,
    promoTitle,
    promoDescription,
    learnMoreLabel,
  } = texts ?? {};
  const { getUniqueValue } = useUniqueWcagValues();
  const titleId = getUniqueValue('retirement-calculator-title');

  const [step, setStep] = useState(RetirementCalculatorStep.BASIC_DATA);
  const [basicData, setBasicData] = useState<RetirementCalculatorSubmitValuesModel>();
  const [savings, setSavings] = useState<SavingsValuesModel>();
  const [monthlyInvestment, setMonthlyInvestment] = useState(DEFAULT_MONTHLY_INVESTMENT);
  const [settingsOverrides, setSettingsOverrides] = useState<RetirementCalculatorSettingsOverridesModel>({});

  const [hasChangedStep, setHasChangedStep] = useState(false);

  const goToStep = useCallback(
    (nextStep: RetirementCalculatorStep) => {
      setHasChangedStep(true);
      setStep(nextStep);
      onStepChange?.(nextStep);
    },
    [onStepChange],
  );

  const hasExplicitLimits = settings?.yearlyContributionLimits !== undefined;
  const limitsFetchingDisabled = limitsSource?.disabled || hasExplicitLimits;
  const fetchedLimits = useContributionLimits(
    limitsSource?.baseUrl,
    limitsFetchingDisabled,
    limitsSource?.fallbackLimits,
    basicData?.[RetirementCalculatorField.EMPLOYMENT_TYPE],
  );

  const resolvedSettings = useMemo(
    () =>
      resolveSettings({
        ...settings,
        ...(hasExplicitLimits ? {} : { yearlyContributionLimits: fetchedLimits }),
      }),
    [fetchedLimits, hasExplicitLimits, settings],
  );

  const calculationSettings = useMemo(
    () =>
      applySettingsOverrides(
        resolvedSettings,
        settingsOverrides,
        basicData?.[RetirementCalculatorField.GENDER],
      ),
    [basicData, resolvedSettings, settingsOverrides],
  );

  const promotedProduct = useMemo(
    () =>
      getPromotedProduct(
        savings,
        basicData?.[RetirementCalculatorField.EMPLOYMENT_TYPE],
        basicData?.[RetirementCalculatorField.MONTHLY_GROSS_SALARY],
      ),
    [basicData, savings],
  );

  const maxMonthlyInvestment = promotedProduct
    ? getMaxMonthlyInvestment(promotedProduct, calculationSettings)
    : MIN_MONTHLY_INVESTMENT;

  const resolvedProjection = useMemo(
    () =>
      calculateProjection(
        basicData,
        savings,
        calculationSettings,
        undefined,
        promotedProduct ? { product: promotedProduct, monthlyInvestment } : undefined,
      ),
    [basicData, calculationSettings, monthlyInvestment, promotedProduct, savings],
  );

  const handleBasicDataSubmit = (values: RetirementCalculatorSubmitValuesModel) => {
    setBasicData(values);
    onBasicDataSubmit?.(values);
    goToStep(RetirementCalculatorStep.SAVINGS);
  };

  const handleSavingsSubmit = (values: SavingsValuesModel) => {
    setSavings(values);
    onSavingsSubmit?.(values, calculateProjection(basicData, values, calculationSettings));
    goToStep(RetirementCalculatorStep.OUTCOME);
  };

  const handleBack = useCallback(
    () =>
      goToStep(
        step === RetirementCalculatorStep.OUTCOME
          ? RetirementCalculatorStep.SAVINGS
          : RetirementCalculatorStep.BASIC_DATA,
      ),
    [goToStep, step],
  );

  const layoutContext = useMemo(
    () => ({
      titleId,
      step,
      component,
      className,
      testId,
      autoFocus: hasChangedStep,
      ...(step !== RetirementCalculatorStep.BASIC_DATA && { onBack: handleBack }),
    }),
    [className, component, handleBack, hasChangedStep, step, testId, titleId],
  );

  const renderStep = () => {
    if (step === RetirementCalculatorStep.OUTCOME) {
      return (
        <RetirementCalculatorOutcomeStep
          basicData={basicData}
          savings={savings}
          settings={resolvedSettings}
          settingsOverrides={settingsOverrides}
          onSettingsOverridesChange={setSettingsOverrides}
          onEdit={goToStep}
          projection={resolvedProjection}
          promotedProduct={promotedProduct}
          payoutYears={calculationSettings.payoutYears}
          monthlyInvestment={monthlyInvestment}
          maxMonthlyInvestment={maxMonthlyInvestment}
          onMonthlyInvestmentChange={setMonthlyInvestment}
          promoTitle={promoTitle}
          promoDescription={promoDescription}
          learnMoreLabel={learnMoreLabel}
          learnMoreUrl={learnMoreUrl}
          onDownloadSummary={onDownloadSummary}
        />
      );
    }

    if (step === RetirementCalculatorStep.SAVINGS) {
      return (
        <RetirementCalculatorSavingsStep
          title={title}
          submitLabel={savingsSubmitLabel}
          initialValues={savings}
          yearlyContributionLimits={calculationSettings.yearlyContributionLimits}
          projection={resolvedProjection}
          onSubmit={handleSavingsSubmit}
          onValuesChange={setSavings}
          disabled={disabled}
          isLoading={isLoading}
        />
      );
    }

    return (
      <RetirementCalculatorBasicDataStep
        title={title}
        submitLabel={submitLabel}
        salaryTooltip={salaryTooltip}
        initialValues={basicData ?? initialValues}
        onSubmit={handleBasicDataSubmit}
        disabled={disabled}
        isLoading={isLoading}
      />
    );
  };

  return (
    <RetirementCalculatorLayoutContext.Provider value={layoutContext}>
      {renderStep()}
    </RetirementCalculatorLayoutContext.Provider>
  );
};
